import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { AnalyticsWasteSummary } from "../../api/types";
import { Modal } from "../Modal";
import { eur, hhmm } from "../../utils/homeFormat";
import { wasteCauseLabel } from "../../utils/wasteCause";

interface ShopWaste {
  locationId: string;
  name: string;
  /** null — the shop's request failed. */
  summary: AnalyticsWasteSummary | null;
}

/**
 * G-204 (owner 2026-11-04): the "Waste" tile opens what was wasted today —
 * per shop, each entry with its time, item, quantity, cause, who logged it
 * and what it cost. There is no waste page to link to, and Analytics shows
 * one shop at a time with its entries folded away, so the answer is a sheet
 * on the home page, read from the same endpoint Analytics uses
 * (`GET /analytics/waste`), asked once per shop when the sheet opens.
 */
export function WasteTodayModal({
  shops,
  from,
  to,
  onClose,
}: {
  shops: Array<{ id: string; name: string }>;
  /** Whole days, "YYYY-MM-DD"; the server reads them in Barcelona time. */
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<ShopWaste[] | null>(null);

  useEffect(() => {
    let live = true;
    Promise.allSettled(shops.map((shop) => api.getAnalyticsWaste({ locationId: shop.id, from, to }))).then(
      (results) => {
        if (!live) return;
        setRows(
          shops.map((shop, i) => {
            const r = results[i];
            return { locationId: shop.id, name: shop.name, summary: r.status === "fulfilled" ? r.value : null };
          }),
        );
      },
    );
    return () => {
      live = false;
    };
  }, [shops, from, to]);

  const nothing = rows !== null && rows.every((r) => r.summary !== null && r.summary.recent.length === 0);

  return (
    <Modal title="Waste today" onClose={onClose}>
      {rows === null ? (
        <p className="muted an-note">Loading…</p>
      ) : nothing ? (
        <p className="muted an-note">Nothing wasted today.</p>
      ) : (
        <div className="home-waste">
          {rows.map((shop) => (
            <section key={shop.locationId} className="home-waste-shop">
              {shops.length > 1 || shop.summary === null ? (
                <div className="an-card-head">
                  <strong>{shop.name}</strong>
                  {shop.summary ? (
                    <span className="home-waste-total">{eur(shop.summary.totalCostEur)}</span>
                  ) : null}
                </div>
              ) : null}
              {shop.summary === null ? (
                <p className="muted an-note">Could not load this shop&apos;s waste.</p>
              ) : shop.summary.recent.length === 0 ? (
                shops.length > 1 ? <p className="muted an-note">Nothing wasted.</p> : null
              ) : (
                <ul className="home-waste-list">
                  {shop.summary.recent.map((entry) => (
                    <li key={entry.id}>
                      <span className="home-order-time">{hhmm(entry.occurredAt)}</span>
                      <span className="home-waste-main">
                        <strong>
                          {entry.quantity} {entry.uom.toLowerCase()} · {entry.itemName}
                        </strong>
                        <span className="muted">
                          {wasteCauseLabel(entry.cause)} · {entry.loggedBy}
                          {entry.reason ? ` · ${entry.reason}` : ""}
                        </span>
                      </span>
                      <span className="home-waste-cost">{entry.costEur == null ? "—" : eur(entry.costEur)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* The endpoint lists the 50 latest entries; the count says what is beyond them. */}
              {shop.summary && shop.summary.entryCount > shop.summary.recent.length ? (
                <p className="muted an-note">
                  and {shop.summary.entryCount - shop.summary.recent.length} more
                </p>
              ) : null}
              {shop.summary && shop.summary.unknownCostEntryCount > 0 ? (
                <p className="muted an-note">
                  {shop.summary.unknownCostEntryCount}{" "}
                  {shop.summary.unknownCostEntryCount === 1 ? "entry" : "entries"} of unknown cost not counted
                </p>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}
